
import { useState } from "react"

import DocViewer, { DocViewerRenderers } from "react-doc-viewer"

import { JsonDisplay } from "@/components/displays/JsonDisplay"
import { MarkdownDisplay } from "@/components/displays/MarkdownDisplay"
import { isImage, isJson, isMsFile, isOfficeFile, isMarkdown, isPdf, isPlainText, isTextMarkdown, isVideo } from "@/lib/mime"
import { useUserSession } from "@session/UserSession"

interface DocumentDisplayProps {
    documentId: string
    name: string
    type: string
    url: string
    mapping?: Record<string, string>
}
export function DocumentDisplay({ documentId, name, type, url, mapping }: Readonly<DocumentDisplayProps>) {
    const { store } = useUserSession()

    if (isVideo(type)) {
        return (
            <div className='flex w-full h-full items-center justify-center'>
                <video src={url} controls style={{ width: "100%" }}>
                    <track kind="captions" src="" label="Captions" />
                </video>
            </div>
        )
    }

    if (isImage(type)) {
        return (
            <div className='flex w-full h-full items-center justify-center'>
                <img src={url} alt={name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
        )
    }

    if (isPdf(type)) {
        return (
            <iframe src={url} height="100%" width="100%" title={`PDF viewer for ${name}`} />
        )
    }

    if (isMsFile(type) || isOfficeFile(type)) {
        const documents = [{ uri: url }]
        return (
            <DocViewer documents={documents} pluginRenderers={DocViewerRenderers} config={{ header: { disableHeader: true, disableFileName: true, retainURLParams: true } }} style={{ width: "100%", height: "100%" }} />
        )
    }

    if (isJson(type)) {
        if (!mapping) {
            return (
                <span>
                    Sorry for the inconvenience but you need a mapping to display that JSON content.
                </span>
            )
        }

        const [text, setText] = useState("")
        if (!text.length) {
            store.objects.getObjectText(documentId)
                .then((response) => {
                    setText(response.text)
                })
        }

        return (
            <div className='w-full h-full overflow-auto p-2'>
                <JsonDisplay content={text} mapping={mapping} />
            </div>
        )
    }

    if (isMarkdown(type) || isTextMarkdown(type) || isPlainText(type)) {
        const [text, setText] = useState("")
        if (!text.length) {
            store.objects.getObjectText(documentId)
                .then((response) => {
                    setText(response.text)
                })
        }

        return (
            <div className='w-full h-full overflow-auto p-2'>
                <MarkdownDisplay content={text} />
            </div>
        )
    }

    return (
        <span>
            Sorry for the inconvenience but {type} is not supported.
        </span>
    )
}